// src/Components/SideNav.js
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { privateRoutes } from "../routes/routes";
import { useGlobalSideNav } from "../hooks/useGlobalSideNav";
import ServiceSelectPrompt from "./ServiceSelectPrompt";
import {
  FaChevronDown,
  FaChevronRight,
  FaLock,
  FaThLarge,
  FaTachometerAlt,
  FaUserCircle,
  FaReceipt,
  FaExternalLinkAlt,
  FaSeedling,
} from "react-icons/fa";
import { FEED_WORLD_SITE_URL, openWebsitePage } from "../apis/ssoApi";
import { matchesShellSearch, useDashboardShown, useShellSearch } from "../shared/shellSearch";
import { BrandName } from "../config/brand";

// The bottom of the sidebar: a small brand line (feedShell.css hides it when the sidebar is
// narrowed to icons). The farm picture that was here was taken out at the user's request (2026-10-27)
// so the menu gets the room.
function SidebarBottom() {
  return (
    <div className="fw-nav-bottom" aria-hidden="true">
      <div className="fw-sidebar-brand">
        <span className="fw-brand-mark"><FaSeedling /></span>
        <span className="fw-brand-copy">
          <strong><BrandName /></strong>
          <span>Global Agri Ecosystem</span>
        </span>
      </div>
    </div>
  );
}

// Services whose sub-pages render inline as a dropdown in the sidebar
const DROPDOWN_SERVICES = ["admin"];

// Section grouping for a single service's own module list (e.g. myFpo's
// My Profile / My Account / Business Plan / ...) — category comes from each
// module's `category` field in routes.js's getModules(). Separate from the
// service-level categories above since these are a different vocabulary.
// SIDENAV_REDESIGN_PLAN.md Part 1: "overview" relabeled "My Profile" (module label itself
// changed in routes.js; this bucket label just needs to stop saying "Overview" too), plus new
// "directory"/"trade" buckets for My Directory/My Trade.
// SIDENAV_REDESIGN_PLAN_V2.md §1 — "about" is its own top-level section (About FPO/About
// Business), "services" for My Services; both new, positioned right after My Profile and before
// Accounts & Finance, per the confirmed structure.
// Dashboard redesign (Phase A): the single "overview" module itself was renamed "My Profile" ->
// "Dashboard" in routes.js (real profile content moves to /home/profile, reachable via Global
// SideNav + Header's profile dropdown instead) -- this bucket label follows it, since "overview"
// only ever contains that one item.
const MODULE_CATEGORY_ORDER = ["overview", "about", "services", "finance", "directory", "trade", "governance", "growth"];
const MODULE_CATEGORY_LABELS = {
  overview: "Dashboard",
  // Generic label — the module row itself already carries the specific "About FPO"/"About
  // Business" text (routes.js), so the section header doesn't need to vary per service.
  about: "About",
  services: "My Services",
  finance: "Accounts & Finance",
  directory: "Directory",
  trade: "Trade & Market",
  governance: "Governance & Compliance",
  growth: "Growth & Development",
  other: "Other",
};

// SIDENAV_REDESIGN_PLAN_V2.md §4 gap 1 — Accounts' children are grouped into always-expanded,
// non-toggleable sub-headers (Registers/Members/Transactions) via each child's `subGroup` field.
// Returns null when no child carries `subGroup` (e.g. Compliances' children), so callers can fall
// back to the existing flat rendering unchanged.
const groupChildrenBySubGroup = (children) => {
  if (!children.some((child) => child.subGroup)) return null;

  const order = [];
  const groups = {};
  children.forEach((child) => {
    const key = child.subGroup || "";
    if (!groups[key]) {
      groups[key] = [];
      order.push(key);
    }
    groups[key].push(child);
  });

  return order.map((subGroup) => ({ subGroup, items: groups[subGroup] }));
};

// Generic grouping: buckets `items` by their `category` field, in `order`,
// with anything unrecognized falling into a trailing "other" bucket.
const groupByCategory = (items, order, labels) => {
  const groups = {};
  items.forEach((item) => {
    const key = item.category && labels[item.category] ? item.category : "other";
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });

  const orderedKeys = [
    ...order.filter((key) => groups[key]),
    ...(groups.other ? ["other"] : []),
  ];

  return orderedKeys.map((key) => ({
    category: key,
    label: labels[key],
    items: groups[key],
  }));
};

// `collapsed` — desktop icon-rail mode (AppShell.jsx's isDesktopCollapsed, toggled by the top
// bar's ☰); hides the label text so only the icons show. Styles: styles/feedShell.css (.fw-nav…).
const SideNav = ({ serviceKeySelected, collapsed = false }) => {
  const location = useLocation();
  const { normalizedRoles, fullUserData, hasEntityAccess } = useAuth();
  const globalNav = useGlobalSideNav();
  const [choosePromptItem, setChoosePromptItem] = useState(null);

  // The top bar's search narrows this menu, except on a dashboard (it narrows the dashboard's
  // own tiles there). A parent row stays when one of its children matches, opened to show it.
  const search = useShellSearch();
  const dashboardShown = useDashboardShown();
  const filtering = !dashboardShown && search.trim() !== "";
  const shows = (label, children) =>
    !filtering || matchesShellSearch(search, label) || !!children?.some((child) => matchesShellSearch(search, child.label));

  // Track which module is expanded (Service-mode's My Account/Compliances/Directory/Trade
  // dropdowns — see the Service-mode branch below; Global mode has no expandable sections of its
  // own). Accordion, not independent toggles: opening one REPLACES expandedKeys rather than
  // appending, so a second dropdown can't be left open alongside it, and every plain (non-
  // dropdown) link's onClick clears it entirely — clicking anything outside the open dropdown
  // closes it, matching how only one should ever be open at a time.
  const [expandedKeys, setExpandedKeys] = useState([]);

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => (prev.includes(key) ? [] : [key]));
  };

  const closeExpanded = () => setExpandedKeys([]);

  // The menu is opened, closed and narrowed to icons from the top bar's ☰ (Header.js/AppShell.jsx).

  // Treat admin/users as part of the Global-mode view — they have no Service-mode sidebar of
  // their own (Administration's Global section already links straight into them).
  const showAllServices =
    !serviceKeySelected ||
    serviceKeySelected === "users" ||
    DROPDOWN_SERVICES.includes(serviceKeySelected);

  // 🔹 Global mode — SIDENAV_REDESIGN_PLAN.md Part 2. Fixed structure (Services / My Account /
  // Directory / Governance & Compliance / Administration / Growth & Reports), resolved by
  // useGlobalSideNav() — replaces the old flat "all services" category browser entirely; that
  // browsing experience now lives on the /services page (ServicesWidget.jsx) that "Services"
  // links to.
  if (showAllServices) {
    const isServicesActive = location.pathname === "/services";
    const isDashboardActive = location.pathname === "/home";
    const isProfileActive = location.pathname === "/home/profile";
    const fixedItems = [
      { key: "dashboard", section: "Dashboard", label: "Dashboard", to: "/home", icon: <FaTachometerAlt />, active: isDashboardActive },
      { key: "profile", section: "My Profile", label: "My Profile", to: "/home/profile", icon: <FaUserCircle />, active: isProfileActive },
      // Membership fee and subscription payments with receipts (MyPaymentsPage.jsx)
      { key: "payments", section: "My Profile", label: "My Payments", to: "/home/payments", icon: <FaReceipt />, active: location.pathname === "/home/payments" },
      { key: "services", section: "Services", label: "Services", to: "/services", icon: <FaThLarge />, active: isServicesActive },
    ].filter((item) => shows(item.label));
    const sections = globalNav.sections
      .map((section) => ({ ...section, items: section.items.filter((item) => shows(item.label)) }))
      .filter((section) => section.items.length > 0);

    return (
      <div className="fw-nav-wrap">
        <ul className="fw-nav">
          {filtering && fixedItems.length === 0 && sections.length === 0 && (
            <li className="fw-nav-empty">Nothing in the menu matches “{search.trim()}”.</li>
          )}
          {fixedItems.map((item, i) => (
            <React.Fragment key={item.key}>
              {!collapsed && item.key !== "dashboard" && fixedItems[i - 1]?.section !== item.section && <li className="fw-nav-label">{item.section}</li>}
              <li className={item.active ? "active" : ""}>
                <Link
                  to={item.to}
                  className="text-decoration-none d-flex align-items-center"
                  title={collapsed ? item.label : undefined}
                >
                  <span className="icon">{item.icon}</span>
                  {!collapsed && <span className="fw-nav-text">{item.label}</span>}
                </Link>
              </li>
            </React.Fragment>
          ))}

          {sections.map((section) => (
            <React.Fragment key={section.key}>
              {!collapsed && <li className="fw-nav-label">{section.label}</li>}
              {section.items.map((item, idx) => {
                if (item.mode === "comingSoon") {
                  return (
                    <li key={idx} title={collapsed ? item.label : "Coming Soon"}>
                      <a
                        href="#"
                        onClick={(e) => e.preventDefault()}
                        className="d-flex align-items-center is-disabled"
                      >
                        <span className="icon">{item.icon}</span>
                        {!collapsed && <span className="fw-nav-text">{item.label}</span>}
                      </a>
                    </li>
                  );
                }

                if (item.mode === "choose") {
                  return (
                    <li key={idx}>
                      <a
                        href="#"
                        className="d-flex align-items-center"
                        title={collapsed ? item.label : undefined}
                        onClick={(e) => { e.preventDefault(); setChoosePromptItem(item); }}
                      >
                        <span className="icon">{item.icon}</span>
                        {!collapsed && <span className="fw-nav-text">{item.label}</span>}
                      </a>
                    </li>
                  );
                }

                const isItemActive = location.pathname === item.path.split("?")[0];
                return (
                  <li key={idx} className={isItemActive ? "active" : ""}>
                    <Link
                      to={item.path}
                      className="text-decoration-none d-flex align-items-center"
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="icon">{item.icon}</span>
                      {!collapsed && <span className="fw-nav-text">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </React.Fragment>
          ))}
        </ul>
        <SidebarBottom />
        <ServiceSelectPrompt item={choosePromptItem} onClose={() => setChoosePromptItem(null)} />
      </div>
    );
  }

  // 🔹 Display modules for other services
  const currentService = privateRoutes.find(
    (route) => route.serviceKey === serviceKeySelected
  );

  if (!currentService) {
    console.warn("SideNav - No service found for:", serviceKeySelected);
    return null;
  }

  const filteredModules = (currentService.getModules
    ? currentService.getModules(normalizedRoles, hasEntityAccess, fullUserData)
    : currentService.modules || [])
    // Org-scoped modules (AGM & Board) are hidden rather than shown greyed-with-a-lock when
    // inaccessible: a lock implies "ask for access", but internal staff can never be granted
    // this one -- the documents belong to an individual organization they don't belong to.
    .filter((module) => !(module.orgScoped && module.locked));

  // Points at this service's OWN /services sub-route (routes.js), not the top-level /services —
  // so clicking it stays inside this service's ServicesLayout/serviceKey context and the
  // sidebar keeps showing this service's structure instead of flipping to the Global one.
  const servicesPath = `/${serviceKeySelected}/services`;
  const isServicesActive = location.pathname === servicesPath;

  // Persistent "My Profile" row — mirrors Global mode's own /home/profile entry so Service mode
  // isn't the odd one out. Points at this service's own /profile sub-route (routes.js), keeping
  // the click inside this ServicesLayout/serviceKey shell instead of jumping to the global Home
  // layout. Rendered right after the Dashboard (overview) group below.
  const profilePath = `/${serviceKeySelected}/profile`;
  const isProfileActive = location.pathname === profilePath;

  // The search (when on) keeps the modules whose name - or one of whose sub-pages' names - matches.
  const groups = groupByCategory(filteredModules, MODULE_CATEGORY_ORDER, MODULE_CATEGORY_LABELS)
    .map((group) => ({ ...group, items: group.items.filter((module) => shows(module.label, module.children)) }));
  const showProfile = shows("My Profile");
  const showServices = shows("My Services");
  const nothingMatches = filtering && !showProfile && !showServices && groups.every((group) => group.items.length === 0);

  return (
    <div className="fw-nav-wrap">
      <ul className="fw-nav">
        {nothingMatches && <li className="fw-nav-empty">Nothing in the menu matches “{search.trim()}”.</li>}
        {groups.map((group) => (
          <React.Fragment key={group.category}>
            {!collapsed && group.items.length > 0 && group.category !== "overview" && (
              <li className="fw-nav-label">{group.label}</li>
            )}
            {group.items.map((module) => {
              // Expandable parent (My Account when nested, My Directory, My Trade, Compliances)
              // — SIDENAV_REDESIGN_PLAN.md Part 1. Accordion state is keyed by label.
              if (module.children) {
                const key = module.label;
                // Searching opens a parent whose sub-pages match, showing only those
                const children = filtering && !matchesShellSearch(search, module.label)
                  ? module.children.filter((child) => matchesShellSearch(search, child.label))
                  : module.children;
                const isExpanded = expandedKeys.includes(key) || (filtering && children !== module.children);
                // Clicking the parent row (My Account, Compliances) navigates to its own landing
                // page and opens it, since both have a real destination (FpoAccountHomePage /
                // Compliances' redirect to Statutory).
                const modulePath = `/${serviceKeySelected}/${module.path}`;
                // Deliberately never highlighted itself — only an actual selected child row gets
                // `.active`, so an open dropdown doesn't read as the current page.
                return (
                  <li key={key} className={isExpanded ? "is-open" : ""}>
                    <Link
                      to={modulePath}
                      className="fw-nav-parent text-decoration-none"
                      title={collapsed ? module.label : undefined}
                      onClick={() => {
                        if (!expandedKeys.includes(key)) toggleExpanded(key);
                      }}
                    >
                      <span className="fw-nav-parent-label d-flex align-items-center">
                        <span className="icon">{module.icon}</span>
                        {!collapsed && <span className="fw-nav-text">{module.label}</span>}
                      </span>
                      {!collapsed && (
                        <span
                          className="fw-nav-arrow"
                          role="button"
                          aria-label={isExpanded ? `Close ${module.label}` : `Open ${module.label}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleExpanded(key);
                          }}
                        >
                          {isExpanded ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                        </span>
                      )}
                    </Link>
                    {isExpanded && !collapsed && (
                      <ul className="fw-subnav">
                        {(() => {
                          // renderChild is shared by the grouped and flat paths below so the two
                          // never drift — only the sub-header wrapping differs.
                          const renderChild = (child) => {
                            // No `path` = informational Coming-Soon item (My Directory/My Trade)
                            // — always rendered disabled, never a Link, regardless of `locked`.
                            if (!child.path) {
                              return (
                                <li key={child.label} title="Coming Soon">
                                  <a href="#" onClick={(e) => e.preventDefault()} className="d-flex align-items-center is-disabled">
                                    <span className="icon">{child.icon}</span>
                                    {child.label}
                                  </a>
                                </li>
                              );
                            }

                            const childPath = `/${serviceKeySelected}/${child.path}`;
                            // `hash` distinguishes Members/Share Transfer, which otherwise share one path
                            const childLinkTo = child.hash ? `${childPath}#${child.hash}` : childPath;
                            const isChildActive = child.hash
                              ? location.pathname.includes(childPath) && location.hash === `#${child.hash}`
                              : location.pathname.includes(childPath);

                            if (child.locked) {
                              return (
                                <li key={child.label} title="You don't have permission to access this">
                                  <a href="#" onClick={(e) => e.preventDefault()} className="d-flex align-items-center is-disabled">
                                    {child.label}
                                    <FaLock size={10} style={{ marginLeft: "auto" }} />
                                  </a>
                                </li>
                              );
                            }

                            return (
                              <li key={child.label} className={isChildActive ? "active" : ""}>
                                <Link to={childLinkTo}>{child.label}</Link>
                              </li>
                            );
                          };

                          const subGroups = groupChildrenBySubGroup(children);
                          if (!subGroups) {
                            return children.map(renderChild);
                          }

                          return subGroups.map((sub, gIdx) => (
                            <React.Fragment key={sub.subGroup || gIdx}>
                              <li className="fw-subnav-label">{sub.subGroup}</li>
                              {sub.items.map(renderChild)}
                            </React.Fragment>
                          ));
                        })()}
                      </ul>
                    )}
                  </li>
                );
              }

              // The Dashboard module points at the bare service path (e.g. "/myFpo"), not a
              // sub-path like every other module — needs an exact match, not .includes(), or it
              // would also read as "active" on every sub-page.
              const modulePath = module.path
                ? `/${serviceKeySelected}/${module.path}`
                : `/${serviceKeySelected}`;
              // `hash` (My Directory/My Trade — both share path:"" with the Dashboard) exists
              // purely to tell these apart for highlighting.
              const linkTo = module.hash ? `${modulePath}#${module.hash}` : modulePath;
              const isActive = module.path
                ? location.pathname.includes(modulePath)
                : location.pathname === modulePath &&
                  (module.hash ? location.hash === `#${module.hash}` : !location.hash);

              if (module.locked) {
                return (
                  <li key={module.label} title={collapsed ? module.label : "You don't have permission to access this"}>
                    <a href="#" onClick={(e) => e.preventDefault()} className="d-flex align-items-center is-disabled">
                      <span className="icon">{module.icon}</span>
                      {!collapsed && <span className="fw-nav-text">{module.label}</span>}
                      {!collapsed && <FaLock size={10} style={{ marginLeft: "auto" }} />}
                    </a>
                  </li>
                );
              }

              // A module that lives on the FEED WORLD website (routes.js `websitePath`, e.g. Loans &
              // Schemes -> Know Your Schemes): a new tab there, signed in, this app staying open.
              if (module.websitePath) {
                return (
                  <li key={module.label}>
                    <a
                      href={`${FEED_WORLD_SITE_URL}${module.websitePath}`}
                      className="text-decoration-none d-flex align-items-center"
                      title={collapsed ? module.label : "Opens on the website"}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        openWebsitePage(module.websitePath);
                      }}
                    >
                      <span className="icon">{module.icon}</span>
                      {!collapsed && <span className="fw-nav-text">{module.label}</span>}
                      {!collapsed && <FaExternalLinkAlt size={10} style={{ marginLeft: "auto", opacity: 0.6 }} />}
                    </a>
                  </li>
                );
              }

              return (
                <li key={module.label} className={isActive ? "active" : ""}>
                  <Link
                    to={linkTo}
                    className="text-decoration-none d-flex align-items-center"
                    title={collapsed ? module.label : undefined}
                    onClick={closeExpanded}
                  >
                    <span className="icon">{module.icon}</span>
                    {!collapsed && <span className="fw-nav-text">{module.label}</span>}
                  </Link>
                </li>
              );
            })}

            {/* My Profile — right after the Dashboard (overview) group. Opens this service's own
                /profile sub-route so it stays inside this SideNav shell. */}
            {group.category === "overview" && showProfile && (
              <>
                {!collapsed && <li className="fw-nav-label">My Profile</li>}
                <li className={isProfileActive ? "active" : ""}>
                  <Link
                    to={profilePath}
                    className="text-decoration-none d-flex align-items-center"
                    title={collapsed ? "My Profile" : undefined}
                    onClick={closeExpanded}
                  >
                    <span className="icon"><FaUserCircle /></span>
                    {!collapsed && <span className="fw-nav-text">My Profile</span>}
                  </Link>
                </li>
              </>
            )}

            {/* My Services — its own section between About and Accounts & Finance
                (SIDENAV_REDESIGN_PLAN_V2.md §1), this service's own /services catalog. */}
            {group.category === "about" && showServices && (
              <>
                {!collapsed && <li className="fw-nav-label">My Services</li>}
                <li className={isServicesActive ? "active" : ""}>
                  <Link
                    to={servicesPath}
                    className="text-decoration-none d-flex align-items-center"
                    title={collapsed ? "My Services" : undefined}
                    onClick={closeExpanded}
                  >
                    <span className="icon"><FaThLarge /></span>
                    {!collapsed && <span className="fw-nav-text">My Services</span>}
                  </Link>
                </li>
              </>
            )}
          </React.Fragment>
        ))}
      </ul>
      <SidebarBottom />
    </div>
  );
};

export default SideNav;
